/**
 * usePlaybackChannel
 *
 * Thin wrapper around the SDK's `useClipSession` hook. All RTCPeerConnection,
 * DataChannel, signaling-WebSocket and MediaSource plumbing now lives inside
 * `@vgps/livekit_web` (since 0.1.11). The CMS keeps this hook signature stable
 * for the existing `ImageListView/PlaybackTab` consumer.
 *
 * Migration notes:
 *  - Old signature: usePlaybackChannel(signalingUrl, deviceSerial)
 *  - New signature: usePlaybackChannel(deviceSerial, channelId='101')
 *    The runtime owns signaling URL via env (REACT_APP_LIVEKIT_SIGNALING_URL).
 *  - Returned fields match the legacy hook:
 *      status, error, videoUrl, progress, slotId, durationMs, buffering,
 *      requestPlayback
 */
import { useCallback } from 'react';
import { useClipSession } from '@vgps/livekit_web/react';

/**
 * @param {string} deviceSerial  - e.g. "862635067769554"
 * @param {string} [channelId]   - e.g. "101" (cam0 HD), "102" (cam0 SD),
 *                                 "201" (cam1 HD), "202" (cam1 SD).
 *                                 Default '101' for backward compatibility.
 * @returns {{ status, error, videoUrl, progress, slotId, durationMs,
 *             buffering, requestPlayback }}
 */
export function usePlaybackChannel(deviceSerial, channelId = '101') {
  const req = deviceSerial ? { deviceSerial, channelId } : null;
  const { session, state } = useClipSession(req);

  const requestPlayback = useCallback((slot, camera) => {
    if (session) session.requestPlayback(slot, camera);
  }, [session]);

  return {
    status: state.status,
    error: state.error,
    videoUrl: state.videoUrl,
    progress: state.progress,
    slotId: state.slotId,
    durationMs: state.durationMs,
    buffering: state.buffering,
    requestPlayback,
  };
}
